import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { Link } from 'react-router-dom';
import "../App.css";
import { RootState } from '../Redux/store';
import { setAuth } from '../Redux/authSlice';
import { translations } from '../utils/translations';
import Charts from './Charts';
import TopSellingProductsPieChart from './TopSellingProductsPieChart';
import TopSellingProductsTable from './TopSellingProductsTable';
import TopCustomersPieChart from './TopCustomersPieChart';
import "./dashboard.css";
import InstallPrompt from './InstallPrompt';

interface MonthData {
    total_sales: number;
    total_expenses: number;
    net_income: number;
}

interface ApiResponse {
    income_and_expenses_by_month: Record<string, MonthData>;
    total_yearly_income: number;
    total_yearly_expenses: number;
    total_yearly_net_income: number;
    current_year: number;
}

const Dashboard = () => {
    const dispatch = useDispatch();
    const [userInfo, setUserInfo] = useState<{ firstName: string; lastName: string; branchLogo: string; branchName: string; }>({ firstName: '', lastName: '', branchLogo: '', branchName: '' });
    const auth = useSelector((state: RootState) => state.auth.value);
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];
    const [totalYearlyPurchases, setTotalYearlyPurchases] = useState<number>(0);
    const [dailySales, setDailySales] = useState<number>(0);
    const [monthlySales, setMonthlySales] = useState<number>(0);
    const [weeklySales, setWeeklySales] = useState<number>(0);
    const [customerCount, setCustomerCount] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [incomeAndExpenses, setIncomeAndExpenses] = useState<{ [key: string]: any }>({}); // New state for income and expenses
    const [currentMonthExpense, setCurrentMonthExpense] = useState<number>(0);
    const [currentMonthIncome, setCurrentMonthIncome] = useState<number>(0);
    const [salesReturn, setSalesReturn] = useState<number>(0);
    const [purchaseReturn, setPurchaseReturn] = useState<number>(0);
    const [userRole, setUserRole] = useState<string>(''); // State for user role
    const [isMobile, setIsMobile] = useState(false); // State to track mobile screen size

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768); // Set isMobile to true if screen width is <= 768px
        };

        window.addEventListener('resize', handleResize); // Add resize event listener
        handleResize(); // Initial check on component mount

        return () => window.removeEventListener('resize', handleResize); // Cleanup on component unmount
    }, []);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const token = getCookie('token');
                if (!token) {
                    dispatch(setAuth(false));
                    return;
                }
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                // Fetch user info
                const userResponse = await axios.get('https://api.sominnovations.xyz/users/user');
                const { first_name, last_name, branch_logo, branch_name, role } = userResponse.data.user;
                setUserInfo({ firstName: first_name, lastName: last_name, branchLogo: branch_logo, branchName: branch_name });
                setUserRole(role); // Set the user role
                dispatch(setAuth(true));

                // After fetching user info, fetch sales and customer data
                fetchSalesData();
                fetchCustomerCount();
                fetchPurchasesData();
                fetchIncomeExpensesData();
                fetchSalesreturnCountData();
                fetchPurchaseturnCountData();
            } catch (e) {
                console.error('Error fetching user data:', e);
                dispatch(setAuth(false));
                setError('Failed to fetch user data');
                setLoading(false);
            }
        };

        const fetchSalesData = async () => {
            try {
                // Helper function to calculate total from sales array
                const calculateTotal = (salesArray: any[]) => {
                    if (!Array.isArray(salesArray)) return 0;
                    return salesArray.reduce((acc: number, sale: any) => acc + (parseFloat(sale.total_amount) || 0), 0);
                };

                const dailySalesResponse = await axios.get('https://api.sominnovations.xyz/Branches/num_Sales/', {
                    params: { period: 'daily' }
                });
                // Check if total_sales exists, otherwise calculate from sales array
                const dailyTotal = dailySalesResponse.data.total_sales !== undefined
                    ? dailySalesResponse.data.total_sales
                    : calculateTotal(dailySalesResponse.data.sales);
                setDailySales(dailyTotal);

                const monthlySalesResponse = await axios.get('https://api.sominnovations.xyz/Branches/num_Sales/', {
                    params: { period: 'monthly' }
                });
                const monthlyTotal = monthlySalesResponse.data.total_sales !== undefined
                    ? monthlySalesResponse.data.total_sales
                    : calculateTotal(monthlySalesResponse.data.sales);
                setMonthlySales(monthlyTotal);

                const weeklySalesResponse = await axios.get('https://api.sominnovations.xyz/Branches/num_Sales/', {
                    params: { period: 'weekly' }
                });
                const weeklyTotal = weeklySalesResponse.data.total_sales !== undefined
                    ? weeklySalesResponse.data.total_sales
                    : calculateTotal(weeklySalesResponse.data.sales);
                setWeeklySales(weeklyTotal);

            } catch (e) {
                console.error('Error fetching sales data:', e);
                setError('Failed to fetch sales data');
            } finally {
                setLoading(false);
            }
        };

        const fetchCustomerCount = async () => {
            try {
                const response = await axios.get('https://api.sominnovations.xyz/Branches/num_Customers/');
                setCustomerCount(response.data.num_Customers); // Set customer count
            } catch (e) {
                console.error('Error fetching customer count:', e);
                setError('Failed to fetch customer count');
            }
        };

        const fetchPurchasesData = async () => {
            try {
                const response = await axios.get('https://api.sominnovations.xyz/Branches/purchases_by_month/');
                setTotalYearlyPurchases(response.data.total_yearly_purchases); // Set total yearly purchases
            } catch (e) {
                console.error('Error fetching purchases data:', e);
                setError('Failed to fetch purchases data');
            }
        };

        const fetchIncomeExpensesData = async () => {
            try {
                const response = await axios.get<ApiResponse>('https://api.sominnovations.xyz/Branches/income-expenses-by-month/');
                const data = response.data.income_and_expenses_by_month;

                // Get the current month in full name format (e.g., "January")
                const currentMonth = new Date().toLocaleString('default', { month: 'long' });

                // Fetch the total expenses and net income for the current month
                const currentMonthData = data[currentMonth];

                if (currentMonthData) {
                    setCurrentMonthExpense(currentMonthData.total_expenses);
                    setCurrentMonthIncome(currentMonthData.net_income);
                } else {
                    // Default values if the current month data is missing
                    setCurrentMonthExpense(0);
                    setCurrentMonthIncome(0);
                }
            } catch (e) {
                console.error('Error fetching income and expenses data:', e);
                setError('Failed to fetch income and expenses data');
            }
        };

        // Fetch the sales return count
        const fetchSalesreturnCountData = async () => {
            try {
                const response = await axios.get('https://api.sominnovations.xyz/Branches/sales-returns-count/');
                setSalesReturn(response.data.total_return_amount);
            } catch (e) {
                console.error('Error fetching income and expenses data:', e);
                setError('Failed to fetch income and expenses data');
            }
        };

        // Fetch the purchase return count
        const fetchPurchaseturnCountData = async () => {
            try {
                const response = await axios.get('https://api.sominnovations.xyz/Branches/purchase-returns-count/');
                setPurchaseReturn(response.data.total_refund_amount);
            } catch (e) {
                console.error('Error fetching income and expenses data:', e);
                setError('Failed to fetch income and expenses data');
            }
        };

        fetchUserData(); // Start fetching user data
    }, [dispatch]);

    const getCookie = (name: string) => {
        const cookieName = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return '';
    };



    const formatCurrency = (value: any) => {
        if (value === undefined || value === null) return 'Ksh 0';

        let numValue: number;

        if (typeof value === 'string') {
            // Remove 'Ksh' or 'ksh' and commas if present
            const cleanStr = value.replace(/ksh/gi, '').replace(/,/g, '').trim();
            numValue = parseFloat(cleanStr);
        } else {
            numValue = value;
        }

        if (isNaN(numValue)) return 'Ksh 0';

        return `Ksh ${numValue.toLocaleString()}`;
    };

    return (
        <div className="container-fluid p-0">
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : auth ? (
                <div>
                    <InstallPrompt />
                    <div className="dashboard-container p-3">
                        <div className="row g-3">
                            {/* Daily Sales Card */}
                            {/* Daily Sales Card */}
                            <div className="col-6 col-md-6 col-lg-3">
                                <Link to="/sales" className={`text-decoration-none ${isMobile ? '' : ''}`}>
                                    <div className="card h-100 border-0 shadow-sm card-hover hover-scale">
                                        <div className="card-body p-2 d-flex align-items-center">
                                            <div className="icon-box bg-success-gradient text-white rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}>
                                                <i className="fa-solid fa-calendar-day"></i>
                                            </div>
                                            <div>
                                                <h6 className="text-muted small mb-1 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{t.dailySales}</h6>
                                                <h5 className="fw-bold text-dark mb-0 fs-6">{formatCurrency(dailySales)}</h5>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>

                            {/* Monthly Sales Card */}
                            {userRole === 'admin' && (
                                <div className="col-6 col-md-6 col-lg-3">
                                    <Link to="/sales" className={`text-decoration-none ${isMobile ? '' : ''}`}>
                                        <div className="card h-100 border-0 shadow-sm card-hover hover-scale">
                                            <div className="card-body p-2 d-flex align-items-center">
                                                <div className="icon-box bg-primary-gradient text-white rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}>
                                                    <i className="fa-solid fa-receipt"></i>
                                                </div>
                                                <div>
                                                    <h6 className="text-muted small mb-1 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{t.monthlySales}</h6>
                                                    <h5 className="fw-bold text-dark mb-0 fs-6">{formatCurrency(monthlySales)}</h5>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            )}

                            {/* Weekly Sales Card */}
                            <div className="col-6 col-md-6 col-lg-3">
                                <Link to="/sales" className={`text-decoration-none ${isMobile ? '' : ''}`}>
                                    <div className="card h-100 border-0 shadow-sm card-hover hover-scale">
                                        <div className="card-body p-2 d-flex align-items-center">
                                            <div className="icon-box bg-warning-gradient text-white rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}>
                                                <i className="fa-solid fa-calendar-week"></i>
                                            </div>
                                            <div>
                                                <h6 className="text-muted small mb-1 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{t.weeklySales}</h6>
                                                <h5 className="fw-bold text-dark mb-0 fs-6">{formatCurrency(weeklySales)}</h5>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>

                            {/* Net Income Card */}
                            {userRole === 'admin' && (
                                <div className="col-6 col-md-6 col-lg-3">
                                    <Link to="/income" className={`text-decoration-none ${isMobile ? '' : ''}`}>
                                        <div className="card h-100 border-0 shadow-sm card-hover hover-scale">
                                            <div className="card-body p-2 d-flex align-items-center">
                                                <div className="icon-box bg-info-gradient text-white rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}>
                                                    <i className="fa-solid fa-chart-line"></i>
                                                </div>
                                                <div>
                                                    <h6 className="text-muted small mb-1 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{t.netIncome}</h6>
                                                    <h5 className="fw-bold text-dark mb-0 fs-6">{formatCurrency(currentMonthIncome)}</h5>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            )}

                            {/* Customer Count Card */}
                            <div className="col-6 col-md-6 col-lg-3">
                                <Link to="/customers" className={`text-decoration-none ${isMobile ? '' : ''}`}>
                                    <div className="card h-100 border-0 shadow-sm card-hover hover-scale">
                                        <div className="card-body p-2 d-flex align-items-center">
                                            <div className="icon-box bg-secondary-gradient text-white rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}>
                                                <i className="fa-solid fa-users"></i>
                                            </div>
                                            <div>
                                                <h6 className="text-muted small mb-1 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{t.customerCount}</h6>
                                                <h5 className="fw-bold text-dark mb-0 fs-6">{customerCount?.toLocaleString() || 0}</h5>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>

                            {/* Yearly Purchases Card */}
                            {userRole === 'admin' && (
                                <div className="col-6 col-md-6 col-lg-3">
                                    <Link to="/purchase" className={`text-decoration-none ${isMobile ? '' : ''}`}>
                                        <div className="card h-100 border-0 shadow-sm card-hover hover-scale">
                                            <div className="card-body p-2 d-flex align-items-center">
                                                <div className="icon-box bg-dark-gradient text-white rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}>
                                                    <i className="fa-solid fa-cart-shopping"></i>
                                                </div>
                                                <div>
                                                    <h6 className="text-muted small mb-1 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{t.yearlyPurchases}</h6>
                                                    <h5 className="fw-bold text-dark mb-0 fs-6">{formatCurrency(totalYearlyPurchases)}</h5>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            )}

                            {/* Monthly Expense Card */}
                            {userRole === 'admin' && (
                                <div className="col-6 col-md-6 col-lg-3">
                                    <Link to="/expenses" className={`text-decoration-none ${isMobile ? '' : ''}`}>
                                        <div className="card h-100 border-0 shadow-sm card-hover hover-scale">
                                            <div className="card-body p-2 d-flex align-items-center">
                                                <div className="icon-box bg-danger-gradient text-white rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}>
                                                    <i className="fa-solid fa-wallet"></i>
                                                </div>
                                                <div>
                                                    <h6 className="text-muted small mb-1 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{t.monthlyExpenses}</h6>
                                                    <h5 className="fw-bold text-dark mb-0 fs-6">{formatCurrency(currentMonthExpense)}</h5>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            )}

                            {/* Purchase Return Count */}
                            <div className="col-6 col-md-6 col-lg-3">
                                <Link to="/purchase-return" className={`text-decoration-none ${isMobile ? '' : ''}`}>
                                    <div className="card h-100 border-0 shadow-sm card-hover hover-scale">
                                        <div className="card-body p-2 d-flex align-items-center">
                                            <div className="icon-box bg-success-gradient text-white rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}>
                                                <i className="fa-solid fa-rotate-left"></i>
                                            </div>
                                            <div>
                                                <h6 className="text-muted small mb-1 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{t.purchaseReturn}</h6>
                                                <h5 className="fw-bold text-dark mb-0 fs-6">{formatCurrency(purchaseReturn)}</h5>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>

                        </div>

                        <div className="row mt-4 g-3">
                            <div className="col-12 col-lg-7">
                                <div className="card border-0 shadow-sm p-3 h-100">
                                    <Charts />
                                </div>
                            </div>
                            <div className="col-12 col-lg-5">
                                <div className="card border-0 shadow-sm p-3 h-100">
                                    <TopSellingProductsPieChart />
                                </div>
                            </div>
                            {userRole === "admin" && (
                                <>
                                    <div className="col-12">
                                        <div className="card border-0 shadow-sm p-3">
                                            <TopSellingProductsTable />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="card border-0 shadow-sm p-3">
                                            <TopCustomersPieChart />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center mt-5">
                    <p>Please log in to view your dashboard.</p>
                </div>
            )}
        </div>
    );
};

export default Dashboard;